import React from 'react'
import { Helmet } from 'react-helmet-async'

interface MetaTagsProps {
  title?: string
  description?: string
  keywords?: string
  canonical?: string
  ogTitle?: string
  ogDescription?: string
  ogImage?: string
  ogUrl?: string
  ogType?: string
  twitterTitle?: string
  twitterDescription?: string
  twitterImage?: string
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player'
  noindex?: boolean
  nofollow?: boolean
}

const DEFAULT_META = {
  title: 'Printboard | Make your vision feel closer',
  description:
    'Printboard turns your vision into a calm, beautiful piece for your home, helping your intentions feel closer and more real.',
  keywords:
    'printed vision board, premium vision board print, custom vision board poster, manifestation board for wall, vision board decor, personalized vision board',
  ogImage: 'https://printboard.io/social-preview.png',
  ogUrl: 'https://printboard.io/',
  ogType: 'website',
  twitterCard: 'summary_large_image' as const,
}

export const MetaTags: React.FC<MetaTagsProps> = ({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  ogType = DEFAULT_META.ogType,
  twitterTitle,
  twitterDescription,
  twitterImage,
  twitterCard = DEFAULT_META.twitterCard,
  noindex = false,
  nofollow = false,
}) => {
  const finalTitle = title || DEFAULT_META.title
  const finalDescription = description || DEFAULT_META.description
  const finalKeywords = keywords || DEFAULT_META.keywords
  const finalOgImage = ogImage || DEFAULT_META.ogImage
  const finalOgUrl = ogUrl || DEFAULT_META.ogUrl

  const robotsContent = [
    noindex ? 'noindex' : 'index',
    nofollow ? 'nofollow' : 'follow',
  ].join(', ')

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="author" content="Printboard" />
      <meta name="robots" content={robotsContent} />

      {/* Canonical URL */}
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={finalOgUrl} />
      <meta property="og:title" content={ogTitle || finalTitle} />
      <meta
        property="og:description"
        content={ogDescription || finalDescription}
      />
      <meta property="og:image" content={finalOgImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Printboard" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:url" content={finalOgUrl} />
      <meta
        name="twitter:title"
        content={twitterTitle || ogTitle || finalTitle}
      />
      <meta
        name="twitter:description"
        content={twitterDescription || ogDescription || finalDescription}
      />
      <meta name="twitter:image" content={twitterImage || finalOgImage} />

      {/* Additional Meta Tags */}
      <meta name="theme-color" content="#3D3B36" />
      <meta name="apple-mobile-web-app-title" content="Printboard" />
      <meta name="application-name" content="Printboard" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
    </Helmet>
  )
}
